import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import { useStaticQuery, graphql, Link } from 'gatsby';
import Img from 'gatsby-image';

import { media } from '../../helpers';
import Blob from '../../../images/page-images/blob-3.inline.svg';
import LeftArrow from '../../../images/icons/arrow-left.inline.svg';
import RightArrow from '../../../images/icons/arrow-right.inline.svg';
import '../../package-styles/slick.css';
import MobileBlob from '../../../images/mobile-blob.inline.svg';

const HomeTopSliderContainer = styled.div`
    position: relative;
    margin-bottom: 17vh;
    ${media.large`margin-bottom: 0;`}
    .arrows-container {
        position: absolute;
        bottom: 5vh;
        right: 5vw;
        width: 43%;
        display: none;
        ${media.large`display: block;`}

        .inner-wrap {
            display: flex;

            .arrow-container {
                flex: 1;

                &.next {
                    text-align: right;
                }

                button {
                    background-color: #f74d33;
                    line-height: 1;
                    padding: 15px;
                    border-radius: 50%;
                    border: 1px solid #f74d33;
                }
            }
        }
    }
    .svg-container {
        position: absolute;
        width: 100%;
        bottom: -20vh;

        svg {
            width: 100%;
            height: auto;
        }

        ${media.large`max-width: 50vw; bottom: 0;`}
        ${media.xl`max-width: 41vw;`}
        ${media.xxxl`max-width: 35vw;`}
        
        .desktop {
            display: none;
            ${media.large`display: block;`}
        }
        .mobile {
            ${media.large`display: none;`}

            svg {
                width: 100%;
                height: fit-content;
            }
        }
    }
    .content-container {
        position: absolute;
        left: 0;
        bottom: 13vh;
        width: 100%;

        ${media.large`left: 5vw; max-width: 90%; bottom: 3vh;`};
        ${media.laptop`bottom: 9vh;`};

        .inner-wrap {
            max-width: 450px;
            padding: 0 20px;
            text-align: center;
            margin: 0 auto;
            ${media.large`text-align: left; margin: 0;`};

            h1 {
                font-size: 3.3rem;
                font-family: neue-kabel, sans-serif;
                font-weight: 800;
                color: #f74d33;
                line-height: 1;
                ${media.large`font-size: 4.7rem;`}
            }

            p {
                font-size: 1rem;
                line-height: 1.5;
            }

            .btn-container {
                a {
                    max-width: 265px;
                    width: 100%;
                    display: inline-block;
                    background-color: #f74d33;
                    line-height: 1;
                    color: #fff;
                    text-align: center;
                    text-decoration: none;
                    font-weight: bold;
                    padding: 20px 0;
                    border-radius: 5px;
                    border: 1px solid #f74d33;
                    font-family: neue-kabel, sans-serif;
                    font-weight: 800;
                }
            }
        }
    }
`;

const SlideContainer = styled.div`
    .image-container {
        max-height: calc(100vh - 76px);
        overflow: hidden;
        position: relative;

        .gatsby-image-wrapper {
            &.mobile-only {
                ${media.medium`display: none;`}
            }
            &.desktop-only {
                display: none;
                ${media.medium`display: block;`}
            }
        }
    }
`;

const HomeTopSlider = () => {
    const data = useStaticQuery(graphql`
        query {
            home: allWpPage(filter: { title: { eq: "Home" } }) {
                edges {
                    node {
                        id
                        home_acf {
                            topSliderSettings {
                                topSliderSlides {
                                    slideImage {
                                        localFile {
                                            childImageSharp {
                                                fluid(
                                                    maxWidth: 1920
                                                    quality: 90
                                                ) {
                                                    ...GatsbyImageSharpFluid
                                                }
                                            }
                                        }
                                    }
                                    mobileSlideImage {
                                        localFile {
                                            childImageSharp {
                                                fluid(
                                                    maxWidth: 992
                                                    quality: 90
                                                ) {
                                                    ...GatsbyImageSharpFluid
                                                }
                                            }
                                        }
                                    }
                                }
                                blobTitle
                                blobCaption
                                blobLink {
                                    title
                                    url
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const sliderRef = useRef(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const [updateCount, setUpdateCount] = useState(0);

    let slides =
        data &&
        data.home.edges[0].node.home_acf.topSliderSettings.topSliderSlides;

    let { blobLink, blobCaption, blobTitle } =
        data.home.edges[0].node.home_acf.topSliderSettings || {};

    var settings = {
        dots: true,
        infinite: true,
        fade: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        afterChange: () => setUpdateCount(updateCount + 1),
        beforeChange: (current, next) => setSlideIndex(next),
    };

    function handleSlideChange(newIndex) {
        setSlideIndex(newIndex);
        sliderRef.current.slickGoTo(newIndex);
    }

    function NextArrow(props) {
        const { className, style, onClick } = props;
        return (
            <div className="arrow-container next">
                <button onClick={() => handleSlideChange(slideIndex + 1)}>
                    <RightArrow />
                </button>
            </div>
        );
    }

    function PrevArrow(props) {
        const { className, style, onClick } = props;
        return (
            <div className="arrow-container prev">
                <button onClick={() => handleSlideChange(slideIndex - 1)}>
                    <LeftArrow />
                </button>
            </div>
        );
    }

    function generateTitle() {
        return { __html: blobTitle };
    }

    return (
        <HomeTopSliderContainer>
            <Slider ref={sliderRef} {...settings}>
                {slides.length > 0 &&
                    slides.map((slide) => {
                        const { slideImage, mobileSlideImage } = slide || {};

                        return (
                            <SlideContainer>
                                <div className="image-container">
                                    <Img
                                        className="desktop-only"
                                        fluid={
                                            slideImage.localFile.childImageSharp
                                                .fluid
                                        }
                                    />
                                    {mobileSlideImage && (
                                        <Img
                                            className="mobile-only"
                                            fluid={
                                                mobileSlideImage.localFile
                                                    .childImageSharp.fluid
                                            }
                                        />
                                    )}
                                </div>
                            </SlideContainer>
                        );
                    })}
            </Slider>

            <div className="svg-container">
                <div className="desktop">
                    <Blob />
                </div>
                <div className="mobile">
                    <MobileBlob />
                </div>

                <div className="content-container">
                    <div className="inner-wrap">
                        <h1 dangerouslySetInnerHTML={generateTitle()}></h1>
                        <p>{blobCaption}</p>
                        <div className="btn-container">
                            <Link to={blobLink.url}>{blobLink.title}</Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="arrows-container">
                <div className="inner-wrap">
                    <PrevArrow />
                    <NextArrow />
                </div>
            </div>
        </HomeTopSliderContainer>
    );
};

export default HomeTopSlider;
